import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar,
  Button,
  Card,
  Divider,
  Input,
  Select,
  Typography,
  Switch,
  Form,
  message,
  Spin,
  Modal
} from 'antd';
import {
  BellOutlined,
  LockOutlined,
  UserOutlined,
  CreditCardOutlined,
  ApiOutlined,
  PlusOutlined,
  MenuOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { AppDispatch, RootState } from '../../../redux/store/store';
import { updateProfile, ProfileDto } from '../../../redux/actions/profile/profile';
import { verifyEmail, resendVerificationCode } from '../../../redux/actions/profile/emailVerification';
import { ValidateTokenAuthReducer } from '../../../redux/actions/auth/login/Login';
import '../../../styles/pages/profile/Profile.css';

const { Title, Text } = Typography;
const { Option } = Select;

const Profile = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [cambiarContrasena, setCambiarContrasena] = useState(false);
  const [verificationModalVisible, setVerificationModalVisible] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string[]>(Array(6).fill(''));
  const [resendingCode, setResendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Fix: Change the ref type to accept Ant Design's InputRef 
  const codeInputRefs = useRef<Array<any>>(Array(6).fill(null));
  
  const dispatch = useDispatch<AppDispatch>();
  const { rex_user } = useSelector((state: RootState) => state.login);
  const { verifying, resending } = useSelector((state: RootState) => state.emailVerification || {
    verifying: false,
    resending: false
  });
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        await dispatch(ValidateTokenAuthReducer());
        setLoadingData(false);
      } catch (error) {
        console.error("Error validating token:", error);
        message.error("Error al cargar los datos del usuario");
        setLoadingData(false);
      }
    };
    
    fetchUserData();
  }, [dispatch]);
  
  useEffect(() => {
    if (rex_user) {
      if (rex_user.imagen_perfil) {
        let imageUrl;
        if (rex_user.imagen_perfil.startsWith('http')) {
          imageUrl = rex_user.imagen_perfil;
        } else {
          const baseUrl = process.env.REACT_APP_API_URL || '';
          const formattedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
          imageUrl = `${formattedBaseUrl}${rex_user.imagen_perfil}`;
        }
        imageUrl = `${imageUrl}?t=${new Date().getTime()}`;
        
        setProfileImage(imageUrl);
        console.log("Setting profile image URL:", imageUrl);
      }
      
      form.setFieldsValue({
        nombre: rex_user.personas?.nombre || '',
        apellidos: rex_user.personas?.apellidos || '',
        email: rex_user.email || '',
        telefono: rex_user.telefono || '',
        pais: rex_user.pais || '',
        timezone: rex_user.timezone || 'America/Lima',
        cambiar_contrasena: false
      });
    }
  }, [rex_user, form]);

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.type.startsWith('image/')) {
        message.error('Por favor seleccione un archivo de imagen válido');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        message.error('La imagen no debe exceder los 5MB');
        return;
      }
      
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        if (event.target && event.target.result) {
          setProfileImage(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeImage = () => {
    setProfileImage(null);
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      const {
        confirmar_contrasena,
        cambiar_contrasena,
        ...dataToSubmit
      } = values;

      if (!cambiar_contrasena) {
        delete dataToSubmit.contrasena;
        delete dataToSubmit.contrasena_actual;
      } else {
        if (!dataToSubmit.contrasena_actual) {
          message.error('Debe ingresar su contraseña actual para cambiarla');
          setLoading(false);
          return;
        }
      }

      const profileData: ProfileDto = {
        nombre: dataToSubmit.nombre,
        apellidos: dataToSubmit.apellidos,
        email: dataToSubmit.email,
        telefono: dataToSubmit.telefono,
        pais: dataToSubmit.pais,
        timezone: dataToSubmit.timezone
      };
      
      if (cambiarContrasena) {
        profileData.contrasena = dataToSubmit.contrasena;
        profileData.contrasena_actual = dataToSubmit.contrasena_actual;
      }
      
      if (imageFile) {
        profileData.imagen_perfil = imageFile;
      }

      console.log('Enviando datos de perfil:', profileData);

      const result = await dispatch(updateProfile(profileData));

      if (result && result.email_pending_verification) {
        setVerificationModalVisible(true);
      } else if (result) {
        message.success('Perfil actualizado exitosamente');
        await dispatch(ValidateTokenAuthReducer());
      }
    } catch (error: unknown) {
      console.error('Error al actualizar el perfil:', error);
      if (error && typeof error === 'object' && 'message' in error &&
        typeof error.message === 'string') {
        message.error(error.message);
      } else {
        message.error('Error al actualizar el perfil');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newVerificationCode = [...verificationCode];
      newVerificationCode[index] = value;
      setVerificationCode(newVerificationCode);
      if (value !== '' && index < 5 && codeInputRefs.current[index + 1]) {
        const inputRef = codeInputRefs.current[index + 1];
        if (inputRef && typeof inputRef.focus === 'function') {
          inputRef.focus();
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newVerificationCode = [...verificationCode];
    for (let i = 0; i < pasteData.length; i++) {
      if (i < 6) {
        newVerificationCode[i] = pasteData[i];
      }
    }
    
    setVerificationCode(newVerificationCode);
    if (pasteData.length < 6 && codeInputRefs.current[pasteData.length]) {
      const inputRef = codeInputRefs.current[pasteData.length];
      if (inputRef && typeof inputRef.focus === 'function') {
        inputRef.focus();
      }
    }
  };

  const handleCopy = () => {
    const code = verificationCode.join('');
    navigator.clipboard.writeText(code)
      .then(() => {
        message.success('Código copiado al portapapeles');
      })
      .catch(() => {
        message.error('Error al copiar el código');
      });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && verificationCode[index] === '' && index > 0) {
      const inputRef = codeInputRefs.current[index - 1];
      if (inputRef && typeof inputRef.focus === 'function') {
        inputRef.focus();
      }
    }
  };

  const handleVerificationSubmit = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      message.error('Por favor ingrese el código de verificación completo');
      return;
    }
  
    setVerifyingCode(true);
    try {
      const codeAsNumber = parseInt(code, 10);
      await dispatch(verifyEmail({ codigo_verificacion: codeAsNumber }));
      setVerificationModalVisible(false);
      message.success('Correo electrónico verificado exitosamente');
      await dispatch(ValidateTokenAuthReducer());
    } catch (error) {
      console.error('Error verifying email:', error);
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleResendCode = async () => {
    setResendingCode(true);
    try {
      await dispatch(resendVerificationCode());
      message.success('Código de verificación reenviado exitosamente');
    } catch (error) {
      console.error('Error resending verification code:', error);
    } finally {
      setResendingCode(false);
    }
  };

  const menuItems = [
    { key: 'profile', icon: <UserOutlined />, title: 'Profile' },
    { key: 'security', icon: <LockOutlined />, title: 'Security' },
    { key: 'notification', icon: <BellOutlined />, title: 'Notification' },
    { key: 'billing', icon: <CreditCardOutlined />, title: 'Billing' },
    { key: 'integration', icon: <ApiOutlined />, title: 'Integration' },
  ];

  return (
    <div>
      <Title level={2} className="settings-title">Settings</Title>
      <Card className="profile-card">
        <div className="flex responsive-layout">
          <div className="menu-toggle" onClick={toggleSidebar}>
            <MenuOutlined />
          </div>

          {sidebarVisible && (
            <div className="sidebar">
              {menuItems.map((item) => (
                <div
                  key={item.key}
                  className={`sidebar-item ${item.key === 'profile' ? 'active' : ''}`}
                >
                  <span className="sidebar-icon">{item.icon}</span>
                  <span className="sidebar-text">{item.title}</span>
                </div>
              ))}
            </div>
          )}

          <div className="main-content">
            {loadingData ? (
              <div className="flex justify-center items-center py-8">
                <Spin tip="Cargando información del perfil..." />
              </div>
            ) : !rex_user ? (
              <div className="flex justify-center items-center py-8">
                <Text type="danger">No se pudo cargar la información del usuario</Text>
              </div>
            ) : (
              <Form
                form={form}
                layout="vertical"
                name="form_editar_perfil"
              >
                <h1 className="content-title">Personal information</h1>
                <div className="profile-image-section">
                  <Avatar
                    key={profileImage || 'default-avatar'}
                    size={100}
                    src={profileImage}
                    className="profile-avatar"
                    icon={!profileImage && <UserOutlined />}
                    alt="Profile picture"
                  />
                  <div className="profile-upload-buttons">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                      accept="image/*"
                    />
                    <Button
                      type="primary"
                      className="rounded-[11px] p-5"
                      icon={<PlusOutlined />}
                      onClick={triggerFileInput}
                    >
                      <p className='font-bold'>Upload Image</p>
                    </Button>
                    <Button
                      className="rounded-[11px] border p-5 border-gray-300 hover:ring-1 hover:ring-blue-500 hover:text-blue-500"
                      onClick={removeImage}
                    >
                      <p className="text-[#525252] font-bold hover:text-blue-500">Remove</p>
                    </Button>
                  </div>
                </div>

                <div className="form-fields">
                  <div className="form-row">
                    <Form.Item
                      name="nombre"
                      label="First name"
                      rules={[
                        {
                          required: true,
                          message: 'Por favor ingrese el nombre',
                        },
                      ]}
                      className="form-group"
                    >
                      <Input className="field-input" />
                    </Form.Item>
                    
                    <Form.Item
                      name="apellidos"
                      label="Last name"
                      rules={[
                        {
                          required: true,
                          message: 'Por favor ingrese los apellidos',
                        },
                      ]}
                      className="form-group"
                    >
                      <Input className="field-input" />
                    </Form.Item>
                  </div>

                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      {
                        required: true,
                        type: 'email',
                        message: 'Por favor ingrese un email válido',
                      },
                    ]}
                    className="form-group full-width"
                  >
                    <Input className="field-input" />
                  </Form.Item>

                  <Form.Item
                    name="telefono"
                    label="Phone number"
                    rules={[
                      {
                        required: true,
                        message: 'Por favor ingrese su numero telefonico',
                      },
                    ]}
                    className="form-group full-width"
                  >
                    <Input 
                      className="field-input phone-input" 
                    />
                  </Form.Item>
                  <Divider />
                  <h1 className="content-title">Address information</h1>
                  <div className="form-row">
                    <Form.Item
                      name="pais"
                      label="Country"
                      className="form-group"
                    >
                      <Select className="field-select">
                        <Option value="United States">
                          <span className="flag-icon">🇺🇸</span> United States
                        </Option>
                        <Option value="Canada">
                          <span className="flag-icon">🇨🇦</span> Canada
                        </Option>
                        <Option value="United Kingdom">
                          <span className="flag-icon">🇬🇧</span> United Kingdom
                        </Option>
                        <Option value="Perú">
                          <span className="flag-icon">🇵🇪</span> Perú
                        </Option>
                      </Select>
                    </Form.Item>
                    
                    <Form.Item
                      name="timezone"
                      label="Timezone"
                      className="form-group"
                    >
                      <Select className="field-select">
                        <Option value="America/Lima">America/Lima</Option>
                        <Option value="America/New_York">America/New_York</Option>
                        <Option value="Europe/Madrid">Europe/Madrid</Option>
                        <Option value="Asia/Tokyo">Asia/Tokyo</Option>
                        <Option value="Australia/Sydney">Australia/Sydney</Option>
                      </Select>
                    </Form.Item>
                  </div>
                  
                  <Divider />
                  <h1 className="content-title">Password</h1>

                  <Form.Item
                    name="cambiar_contrasena"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="Cambiar Contraseña"
                      unCheckedChildren="No Cambiar Contraseña"
                      onChange={(checked) => setCambiarContrasena(checked)}
                    />
                  </Form.Item>

                  {cambiarContrasena && (
                    <div className="border p-4 rounded mb-4 bg-gray-50">
                      <p className="text-blue-600 mb-2">
                        Para cambiar la contraseña, primero debe verificar la contraseña actual
                      </p>

                      <Form.Item
                        name="contrasena_actual"
                        label="Contraseña Actual"
                        rules={[
                          {
                            required: true,
                            message: 'Por favor ingrese su contraseña actual',
                          }
                        ]}
                        hasFeedback
                      >
                        <Input.Password placeholder="Ingrese su contraseña actual" />
                      </Form.Item>

                      <div className="form-row">
                        <Form.Item
                          name="contrasena"
                          label="Nueva Contraseña"
                          rules={[
                            {
                              required: true,
                              message: 'Por favor ingrese una nueva contraseña',
                            },
                            {
                              min: 6,
                              message: 'La contraseña debe tener al menos 6 caracteres',
                            },
                          ]}
                          hasFeedback
                          className="form-group"
                        >
                          <Input.Password placeholder="Ingrese una nueva contraseña" />
                        </Form.Item>
                        <Form.Item
                          name="confirmar_contrasena"
                          label="Confirmar Nueva Contraseña"
                          dependencies={['contrasena']}
                          hasFeedback
                          className="form-group"
                          rules={[
                            {
                              required: true,
                              message: 'Por favor confirme su nueva contraseña',
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('contrasena') === value) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error('Las contraseñas no coinciden'));
                              },
                            }),
                          ]}
                        >
                          <Input.Password placeholder="Confirme su nueva contraseña" />
                        </Form.Item>
                      </div>
                    </div>
                  )}

                  <div className="save-button-container">
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleSave}
                      loading={loading}
                      className="rounded-[11px] font-bold"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </div>
        </div>
      </Card>
      
      <Modal
        title={null}
        footer={null}
        open={verificationModalVisible}
        closable={false}
        centered
        width={500}
        className="verification-modal"
      >
        <div className="p-4 flex flex-col items-center">
          <h1 className="text-2xl font-bold mb-5 text-center">Verifica tu correo</h1>
          <p className="text-base mb-6 text-center">
            Se ha enviado un código de verificación de 6 dígitos a tu correo electrónico. 
            Copia y pégalo en la siguiente sección:
          </p>
          
          <div className="flex justify-center items-center mb-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <Input
                key={index}
                maxLength={1}
                value={verificationCode[index]}
                onChange={(e) => handleVerificationCodeChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="text-center m-1 h-14 w-12 text-xl"
                ref={(el) => {
                  codeInputRefs.current[index] = el;
                }}
              />
            ))}
            <Button
              onClick={handleCopy}
              className="ml-2 h-14"
              icon={<CopyOutlined />}
            />
          </div>
          
          <div className="flex flex-col w-full gap-3">
            <Button
              type="primary"
              onClick={handleVerificationSubmit}
              loading={verifyingCode}
              className="h-11 rounded-lg"
            >
              Verificar Código
            </Button>
            
            <div className="flex justify-center items-center">
              <p className="mr-2">¿No recibiste el código?</p>
              <Button 
                type="link" 
                onClick={handleResendCode}
                loading={resendingCode}
              >
                Reenviar
              </Button>
            </div>
            
            <Button
              onClick={() => setVerificationModalVisible(false)}
              className="h-11 rounded-lg"
            >
              Volver al perfil
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;